import React, { useState, useEffect, useMemo } from 'react';
import { useApi } from '../../hooks/useApi';
import FilterDropdown from '../common/FilterDropdown';
import SearchableDropdown from '../common/SearchableDropdown';
import { useAppState } from '../../context/AppStateContext';

const ClubModal = ({ club = null, onClose, onSuccess }) => {
    const { get, post, put } = useApi();
    const { showToast } = useAppState();
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const initialCategory = club ? (club.category || 'clubs') : 'clubs';
    const [activityType, setActivityType] = useState(initialCategory === 'cocurricular' ? 'cocurricular' : 'extracurricular');
    const [formData, setFormData] = useState({
        name: club ? club.name : '',
        description: club ? (club.description || '') : '',
        teacher: club ? (club.teacher?.id || club.teacher || '') : '',
        category: initialCategory
    });

    const ACTIVITY_TYPE_OPTIONS = [
        { name: 'Extra-curricular Activity', value: 'extracurricular' },
        { name: 'Co-curricular Activity', value: 'cocurricular' }
    ];

    const SUB_CATEGORY_OPTIONS = [
        { name: 'Clubs & Societies', value: 'clubs' },
        { name: 'Games & Sports', value: 'sports' },
        { name: 'Music & Instruments', value: 'instruments' }
    ];

    const handleTypeChange = (typeVal) => {
        setActivityType(typeVal);
        if (typeVal === 'cocurricular') {
            setFormData(prev => ({ ...prev, category: 'cocurricular' }));
        } else {
            setFormData(prev => ({ ...prev, category: prev.category === 'cocurricular' ? 'clubs' : prev.category }));
        }
    };

    useEffect(() => {
        const cat = club ? (club.category || 'clubs') : 'clubs';
        setActivityType(cat === 'cocurricular' ? 'cocurricular' : 'extracurricular');
        setFormData({
            name: club ? club.name : '',
            description: club ? (club.description || '') : '',
            teacher: club ? (club.teacher?.id || club.teacher || '') : '',
            category: cat
        });
    }, [club]);

    useEffect(() => {
        let isMounted = true;
        const fetchTeachers = async () => {
            try {
                const data = await get('/api/teachers/');
                const list = data.results || data || [];
                if (isMounted) {
                    setTeachers(list);
                }
            } catch (error) {
                console.error('Error fetching teachers:', error);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };
        fetchTeachers();
        return () => { isMounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                name: formData.name,
                description: formData.description,
                category: formData.category,
                teacher: formData.teacher ? parseInt(formData.teacher, 10) : null
            };

            if (club) {
                await put(`/api/events/clubs/${club.id}/`, payload);
            } else {
                await post('/api/events/clubs/', payload);
            }
            onSuccess?.();
            showToast(club ? 'Activity protocol updated successfully' : 'Activity initialized successfully', 'success');
            onClose();
        } catch (error) {
            console.error('Error saving club:', error);
            const errorMsg = error.response?.data?.detail || 
                             (error.response?.data && typeof error.response.data === 'object' ? JSON.stringify(error.response.data) : null) || 
                             error.message || 
                             'Could not save activity protocol';
            showToast(errorMsg, 'error');
        }
    };

    const teacherOptions = useMemo(() => {
        return (teachers || []).map(t => ({
            id: t.id.toString(),
            name: t.full_name || `${t.first_name || ''} ${t.last_name || ''}`.trim() || `Teacher #${t.id}`
        }));
    }, [teachers]);

    const CATEGORY_OPTIONS = [
        { name: 'Clubs & Societies', value: 'clubs' },
        { name: 'Games & Sports', value: 'sports' },
        { name: 'Music & Instruments', value: 'instruments' }
    ];

    if (loading && !club) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}></div>
            <div className="relative bg-white w-full max-w-xl rounded-[3rem] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 flex flex-col max-h-[90vh]">
                <div className="p-6 border-b border-slate-50 flex-shrink-0 flex items-start justify-between">
                    <div>
                        <h2 className="text-2xl font-black text-[#18216D] italic uppercase tracking-tighter">
                            {club ? 'Edit Activity Protocol' : 'Establish New Activity'}
                        </h2>
                        <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mt-1">Institutional Extracurricular Settings</p>
                    </div>
                    <button
                        onClick={onClose}
                        type="button"
                        className="text-slate-400 hover:text-slate-600 transition-colors p-2 rounded-lg hover:bg-slate-50"
                    >
                        <i className="fas fa-times text-xl"></i>
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto custom-scrollbar">
                    <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">Activity Designation</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                            className="w-full bg-white border-2 border-slate-300 rounded-2xl px-6 py-4 text-sm font-bold text-[#18216D] focus:ring-4 focus:ring-indigo-50 transition-all outline-none"
                            placeholder="e.g., Coding & Robotics"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">Mission Statement / Description</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full bg-white border-2 border-slate-300 rounded-2xl px-6 py-4 text-sm font-bold text-[#18216D] focus:ring-4 focus:ring-indigo-50 transition-all h-32 outline-none"
                            placeholder="Brief outline of activity objectives..."
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">Activity Classification</label>
                        <FilterDropdown
                            value={activityType}
                            onChange={handleTypeChange}
                            options={ACTIVITY_TYPE_OPTIONS}
                            placeholder="Select Classification"
                            className="w-full"
                        />
                    </div>
                    {activityType === 'extracurricular' && (
                        <div>
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">Extracurricular Sub-Category</label>
                            <FilterDropdown
                                value={formData.category}
                                onChange={(val) => setFormData({ ...formData, category: val })}
                                options={SUB_CATEGORY_OPTIONS}
                                placeholder="Select Sub-Category"
                                className="w-full"
                            />
                        </div>
                    )}
                    <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">Assigned Patron</label>
                        <SearchableDropdown
                            options={teacherOptions}
                            selectedValues={formData.teacher ? [formData.teacher.toString()] : []}
                            onChange={(val) => setFormData({ ...formData, teacher: val })}
                            placeholder="No Patron Assigned (Vacant)"
                            className="w-full"
                        />
                    </div>
                    <div className="pt-6 flex gap-4 flex-shrink-0">
                        <button type="button" onClick={onClose} className="flex-1 py-4 bg-slate-50 text-slate-400 rounded-2xl text-[10px] font-black uppercase tracking-widest">Cancel</button>
                        <button type="submit" className="flex-1 py-4 bg-[#18216D] text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-indigo-900/10">{club ? 'Update Activity' : 'Create Activity'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ClubModal;
